import { useMemo, type CSSProperties, type ReactNode, type SVGProps } from 'react';

/**
 * Onboarding v2 (Apple-style) shared primitives — ported from the Claude
 * Design export. SF-symbol-inspired icons, the CIIMAR mark, Apple Fitness
 * activity rings, a single progress ring and an initials avatar. Visual only;
 * no data or app logic lives here.
 */

const ICONS: Record<string, ReactNode> = {
  home: <path d="M3 11L10 4l7 7v6a1 1 0 0 1-1 1h-3v-5H7v5H4a1 1 0 0 1-1-1v-6Z" />,
  check: <path d="M4.5 10.5l3.5 3.5 7.5-8" />,
  user: (
    <>
      <circle cx="10" cy="7" r="3.2" />
      <path d="M3.8 17c.8-3 3.3-4.7 6.2-4.7s5.4 1.7 6.2 4.7" />
    </>
  ),
  users: (
    <>
      <circle cx="8" cy="7.5" r="2.8" />
      <path d="M2.8 16.3c.5-2.5 2.7-3.8 5.2-3.8s4.7 1.3 5.2 3.8" />
      <circle cx="14.5" cy="6.5" r="2.2" />
      <path d="M14.5 11c2.2.1 4 1.4 4.4 3.3" />
    </>
  ),
  bolt: <path d="M11.5 2.5L4 11.5h5l-1 6 7-9h-5l1.5-6Z" />,
  list: (
    <>
      <path d="M7 5h11M7 10h11M7 15h11" />
      <circle cx="3.7" cy="5" r="1" fill="currentColor" />
      <circle cx="3.7" cy="10" r="1" fill="currentColor" />
      <circle cx="3.7" cy="15" r="1" fill="currentColor" />
    </>
  ),
  bell: (
    <>
      <path d="M5.5 14V9.2a4.5 4.5 0 0 1 9 0V14l1.6 2H3.9l1.6-2Z" />
      <path d="M8.5 17.5a1.5 1.5 0 0 0 3 0" />
    </>
  ),
  search: (
    <>
      <circle cx="9" cy="9" r="5.2" />
      <path d="M13 13l4 4" />
    </>
  ),
  cog: (
    <>
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 2.5v2.4M10 15.1v2.4M2.5 10H5M15 10h2.5M4.7 4.7l1.7 1.7M13.5 13.5l1.7 1.7M15.3 4.7l-1.7 1.7M6.5 13.5l-1.7 1.7" />
    </>
  ),
  plus: <path d="M10 4v12M4 10h12" />,
  arrowR: <path d="M5 10h10M11 6l4 4-4 4" />,
  file: (
    <>
      <path d="M5 3h6.5L16 7.5V17a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M11.5 3v4H16" />
    </>
  ),
  key: (
    <>
      <circle cx="13.5" cy="10" r="3" />
      <path d="M10.5 10H3v2h2v2h2v-2h3.5" />
    </>
  ),
  card: (
    <>
      <rect x="3" y="6" width="14" height="10" rx="2.2" />
      <path d="M3 9.5h14M6 13h2.5" />
    </>
  ),
  chip: (
    <>
      <rect x="5" y="5" width="10" height="10" rx="2" />
      <path d="M8 5V3M12 5V3M8 17v-2M12 17v-2M3 8h2M3 12h2M15 8h2M15 12h2" />
    </>
  ),
  flag: <path d="M5 17V3M5 4h9l-2 3.5L14 11H5" />,
  cal: (
    <>
      <rect x="3" y="5" width="14" height="13" rx="2.5" />
      <path d="M3 9h14M7 3v4M13 3v4" />
    </>
  ),
  chev: <path d="M7 5l5 5-5 5" />,
  chevD: <path d="M5 8l5 5 5-5" />,
  info: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 9v4.5M10 6.5v.5" />
    </>
  ),
  moon: <path d="M16 11.5A6.5 6.5 0 0 1 8.5 4 6.5 6.5 0 1 0 16 11.5Z" />,
  wifi: (
    <>
      <path d="M2 6.5C6.5 3 13.5 3 18 6.5M4.5 9.5C7.5 7 12.5 7 15.5 9.5M7 12.5c1.5-1 4.5-1 6 0" />
      <circle cx="10" cy="15.5" r="0.9" fill="currentColor" />
    </>
  ),
  spark: <path d="M10 3v3M10 14v3M3 10h3M14 10h3" />,
  book: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h4v14H6a2 2 0 0 0-2 2V5Z" />
      <path d="M16 5a2 2 0 0 0-2-2h-4v14h4a2 2 0 0 1 2 2V5Z" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="14" height="10" rx="2" />
      <path d="M3.5 6l6.5 5 6.5-5" />
    </>
  ),
  chat: (
    <path d="M3.5 5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H10l-3.5 3v-3H3.5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
  ),
  grid: (
    <>
      <rect x="3" y="3" width="6" height="6" rx="1.5" />
      <rect x="11" y="3" width="6" height="6" rx="1.5" />
      <rect x="3" y="11" width="6" height="6" rx="1.5" />
      <rect x="11" y="11" width="6" height="6" rx="1.5" />
    </>
  ),
  waves: (
    <path d="M2 7c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0M2 12c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0M2 17c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0" />
  ),
  headset: (
    <>
      <path d="M4 12V9a6 6 0 0 1 12 0v3" />
      <rect x="3" y="11" width="3.5" height="6" rx="1" />
      <rect x="13.5" y="11" width="3.5" height="6" rx="1" />
      <path d="M16 17a3 3 0 0 1-3 3h-1" />
    </>
  ),
  arrow: <path d="M10 4v12M5 11l5 5 5-5" />,
  car: (
    <>
      <path d="M3 11l1.6-4a2 2 0 0 1 1.9-1.3h7a2 2 0 0 1 1.9 1.3L17 11M3 11h14v4a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4Z" />
      <circle cx="6.5" cy="13.5" r="0.9" fill="currentColor" />
      <circle cx="13.5" cy="13.5" r="0.9" fill="currentColor" />
    </>
  ),
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'stroke'> {
  name: string;
  size?: number;
  stroke?: number;
}

export function Icon({ name, size = 16, stroke = 1.7, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {ICONS[name] ?? null}
    </svg>
  );
}

/** The CIIMAR mark — a rounded squircle with concentric waves forming a "C". */
export function Logo({ size = 36 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
      <defs>
        <linearGradient id="onb2-lg-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="oklch(0.55 0.14 220)" />
          <stop offset="1" stopColor="oklch(0.35 0.10 245)" />
        </linearGradient>
      </defs>
      <path
        d="M20 1.5h0c10.5 0 18.5 8 18.5 18.5s-8 18.5-18.5 18.5S1.5 30.5 1.5 20 9.5 1.5 20 1.5Z"
        fill="url(#onb2-lg-bg)"
      />
      <path
        d="M28 14.5a8 8 0 1 0 0 11"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M28 17.5a4.5 4.5 0 1 0 0 5"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
        opacity="0.5"
      />
    </svg>
  );
}

const RING_COLORS = ['oklch(0.62 0.18 25)', 'oklch(0.72 0.18 145)', 'oklch(0.68 0.18 220)'];

/** Apple Fitness-style three concentric activity rings. */
export function ActivityRings({
  size = 160,
  values = [0.6, 0.78, 0.4],
  colors = RING_COLORS,
  stroke = 14,
  gap = 4,
}: {
  size?: number;
  values?: number[];
  colors?: string[];
  stroke?: number;
  gap?: number;
}) {
  const half = size / 2;
  const arcs = values.map((v, i) => {
    const r = half - stroke / 2 - i * (stroke + gap);
    const C = 2 * Math.PI * r;
    return { r, C, dash: C * Math.min(1, v), color: colors[i] };
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {arcs.map((a, i) => (
        <g key={i} transform={`rotate(-90 ${half} ${half})`}>
          <circle
            cx={half}
            cy={half}
            r={a.r}
            fill="none"
            stroke={a.color}
            strokeOpacity="0.18"
            strokeWidth={stroke}
          />
          <circle
            cx={half}
            cy={half}
            r={a.r}
            fill="none"
            stroke={a.color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${a.dash} ${a.C}`}
          />
        </g>
      ))}
    </svg>
  );
}

/** A single progress ring. */
export function Ring({
  size = 64,
  stroke = 6,
  pct = 60,
  color = 'var(--accent)',
}: {
  size?: number;
  stroke?: number;
  pct?: number;
  color?: string;
}) {
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(0,0,0,0.08)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={C}
        strokeDashoffset={C * (1 - pct / 100)}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/** Initials avatar with a deterministic gradient from the name. */
export function Avatar({
  name = 'AB',
  size = 28,
  hue,
}: {
  name?: string;
  size?: number;
  hue?: number;
}) {
  const initials = useMemo(
    () =>
      name
        .split(/\s+/)
        .map((s) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase(),
    [name],
  );
  const h = hue ?? (name.charCodeAt(0) * 17) % 360;
  const style: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: `linear-gradient(135deg, oklch(0.58 0.10 ${h}), oklch(0.34 0.08 ${(h + 30) % 360}))`,
    display: 'grid',
    placeItems: 'center',
    color: 'white',
    fontWeight: 600,
    fontSize: size * 0.36,
    letterSpacing: '-0.01em',
  };
  return <div style={style}>{initials}</div>;
}
